import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  BellRing,
  CalendarClock,
  CheckCircle2,
  Clock,
  Edit3,
  MessageCircle,
  PauseCircle,
  Pill,
  Plus,
  Save,
  Trash2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { useLang } from "../../context/LangContext";
import { useV2Session } from "../context/V2SessionContext";
import { getV2Copy } from "../content";
import { V2_DEFAULT_DAILY_SYSTEM_REMINDER, V2_REMINDER_CHANNELS } from "../lib/v2Schema";
import {
  calculateNextReminderTime,
  formatNextReminder,
  getDueReminders,
  getFrequencyLabel,
  getReminderTimesLabel,
  getReminderChannelLabel,
  getTimesPerDayLabel,
  shouldShowWhatsappNotice,
  V2_REMINDER_EVENT_STATUS,
  V2_REMINDER_FREQUENCIES,
  V2_REMINDER_TIMES_PER_DAY_OPTIONS,
  V2_WEEKDAY_KEYS,
} from "../lib/v2ReminderUtils";
import {
  calculateNextReminderRun,
  formatServerReminderTime,
  getBrowserTimezone,
} from "../lib/v2ReminderSchedule";
import {
  deleteV2ServerMedicationReminder,
  migrateLocalV2RemindersToServer,
  setV2ServerReminderActive,
  syncV2MedicationReminder,
  syncV2NotificationSettings,
  V2_SERVER_SYNC_STATUS,
} from "../lib/v2ReminderRepository";
import {
  channelIncludesWhatsApp,
  createWhatsAppFunctionBody,
  maskWhatsAppNumberForDisplay,
  validateWhatsAppReminderSettings,
} from "../lib/v2WhatsAppUtils";
import {
  createV2MedicationReminder,
  createV2ReminderEvent,
  deleteV2MedicationReminder,
  loadV2MedicationReminders,
  loadV2ReminderEvents,
  setV2MedicationReminderActive,
  updateV2MedicationReminder,
} from "../lib/v2Storage";
import {
  detectPushSupport,
  disablePushNotifications,
  enablePushNotifications,
  getPermissionStatus,
  reconcilePushSubscription,
  sendTestPushNotification,
} from "../lib/v2PushNotifications";

const weekdayLabels = {
  sun: "Sun",
  mon: "Mon",
  tue: "Tue",
  wed: "Wed",
  thu: "Thu",
  fri: "Fri",
  sat: "Sat",
};

const initialForm = {
  medication_name: "",
  dose: "",
  times_per_day: 1,
  reminder_times: ["19:30"],
  reminder_time: "19:30",
  frequency: V2_REMINDER_FREQUENCIES.daily,
  specific_days: [],
  once_date: "",
  active: true,
  channel: V2_REMINDER_CHANNELS.app,
};

const WHATSAPP_UI_STATUS = {
  sending: "Sending test…",
  sent: "Test message sent.",
  dryRun: "Dry run completed. Provider is not sending real messages yet.",
  disabled: "WhatsApp provider is not configured.",
  failed: "Failed to send WhatsApp reminder.",
};

const reminderFormHelp = {
  dose: "Optional. This stays inside the app and is not included in WhatsApp messages.",
  timesPerDay: "How many reminder times to show for each selected day.",
  frequency: "Choose whether this repeats daily, on selected weekdays, or once.",
};

export default function V2Reminders() {
  const { lang } = useLang();
  const copy = getV2Copy(lang);
  const { isGuest, settings, saveSettings } = useV2Session();
  const [reminders, setReminders] = useState(() => loadV2MedicationReminders({ isGuest }));
  const [events, setEvents] = useState(() => loadV2ReminderEvents({ isGuest }));
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [editingId, setEditingId] = useState("");
  const [pushStatus, setPushStatus] = useState(() => createInitialPushStatus());
  const [dueReminders, setDueReminders] = useState([]);
  const [dueSystemReminder, setDueSystemReminder] = useState(null);
  const [whatsappStatus, setWhatsappStatus] = useState("");
  const [serverSync, setServerSync] = useState({
    status: V2_SERVER_SYNC_STATUS.syncing,
    message: "Automatic scheduling is syncing",
    nextRunAt: "",
  });

  const dailySystemReminder = settings?.reminders?.daily_system_reminder || V2_DEFAULT_DAILY_SYSTEM_REMINDER;
  const pushNotificationsEnabled = Boolean(settings?.reminders?.push_notifications_enabled);
  const appRemindersEnabled = Boolean(settings?.reminders?.enabled);
  const hasWhatsappChannel = reminders.some((reminder) => shouldShowWhatsappNotice(reminder.channel));
  const whatsappSettingsValidation = validateWhatsAppReminderSettings(settings?.reminders || {});
  const whatsappRequested =
    Boolean(settings?.reminders?.whatsapp_reminders_enabled) ||
    channelIncludesWhatsApp(settings?.reminders?.channel);
  const whatsappSettingsComplete = whatsappRequested && whatsappSettingsValidation.valid;
  const canShowTestWhatsAppButton =
    Boolean(settings?.reminders?.whatsapp_reminders_enabled) &&
    Boolean(settings?.reminders?.whatsapp_number) &&
    Boolean(settings?.reminders?.whatsapp_consent) &&
    whatsappSettingsValidation.valid;
  const formWhatsappValidation = validateWhatsAppReminderSettings({
    ...(settings?.reminders || {}),
    channel: form.channel,
  });
  const maskedWhatsAppNumber = maskWhatsAppNumberForDisplay(settings?.reminders?.whatsapp_number);
  const nextServerRunAt = useMemo(() => getNextServerRunAt(reminders), [reminders]);

  const recentEvents = useMemo(() => events.slice(0, 6), [events]);

  useEffect(() => {
    setReminders(loadV2MedicationReminders({ isGuest }));
    setEvents(loadV2ReminderEvents({ isGuest }));
  }, [isGuest]);

  useEffect(() => {
    let cancelled = false;

    const migrate = async () => {
      setServerSync(
        getAutomaticSchedulingStatus({
          whatsappSettingsComplete,
          whatsappRequested,
          hasWhatsappChannel,
          syncing: true,
          synced: false,
          nextRunAt: nextServerRunAt,
        })
      );

      const result = await migrateLocalV2RemindersToServer({
        settings,
        reminders,
        isGuest,
      });
      if (cancelled) return;

      setServerSync(
        getAutomaticSchedulingStatus({
          whatsappSettingsComplete,
          whatsappRequested,
          hasWhatsappChannel,
          syncing: false,
          synced: result.ok,
          nextRunAt: nextServerRunAt,
        })
      );
    };

    migrate();
    return () => {
      cancelled = true;
    };
  }, [hasWhatsappChannel, isGuest, nextServerRunAt, reminders, settings, whatsappRequested, whatsappSettingsComplete]);

  useEffect(() => {
    let cancelled = false;
    reconcilePushSubscription().then((result) => {
      if (cancelled) return;
      setPushStatus(createPushStatusFromResult(result));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const runDueCheck = async () => {
      const now = new Date();
      const due = appRemindersEnabled ? getDueReminders(reminders, now, 1) : [];
      setDueReminders(due);

      const systemDue = getDueSystemReminder(dailySystemReminder, now);
      setDueSystemReminder(appRemindersEnabled ? systemDue : null);
    };

    runDueCheck();
    const intervalId = window.setInterval(runDueCheck, 30000);
    return () => window.clearInterval(intervalId);
  }, [appRemindersEnabled, dailySystemReminder, reminders]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const updateTimesPerDay = (value) => {
    const timesPerDay = Number(value);
    setForm((current) => {
      const currentTimes = Array.isArray(current.reminder_times) ? current.reminder_times : [current.reminder_time || "19:30"];
      const fallback = currentTimes[0] || "19:30";
      const reminderTimes = Array.from({ length: timesPerDay }, (_, index) => currentTimes[index] || fallback);
      return {
        ...current,
        times_per_day: timesPerDay,
        reminder_times: reminderTimes,
        reminder_time: reminderTimes[0],
      };
    });
    setErrors((current) => ({ ...current, times_per_day: undefined, reminder_times: undefined }));
  };

  const updateReminderTime = (index, value) => {
    setForm((current) => {
      const currentTimes = Array.isArray(current.reminder_times) ? [...current.reminder_times] : [current.reminder_time || "19:30"];
      currentTimes[index] = value;
      return {
        ...current,
        reminder_times: currentTimes,
        reminder_time: currentTimes[0] || current.reminder_time,
      };
    });
    setErrors((current) => ({ ...current, reminder_times: undefined }));
  };

  const toggleSpecificDay = (day) => {
    setForm((current) => {
      const exists = current.specific_days.includes(day);
      return {
        ...current,
        specific_days: exists
          ? current.specific_days.filter((item) => item !== day)
          : [...current.specific_days, day],
      };
    });
    setErrors((current) => ({ ...current, specific_days: undefined }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const result = editingId
      ? updateV2MedicationReminder(editingId, form, { isGuest })
      : createV2MedicationReminder(form, { isGuest });

    if (!result.ok) {
      setErrors(result.errors);
      toast.error("Check the reminder details.");
      return;
    }

    setReminders(loadV2MedicationReminders({ isGuest }));
    resetForm();
    const syncResult = await syncV2MedicationReminder(result.reminder);
    if (!syncResult.ok) {
      const message = "Saved on this device, but automatic WhatsApp scheduling could not be activated.";
      setServerSync({ status: V2_SERVER_SYNC_STATUS.unavailable, message, nextRunAt: "" });
      toast.warning(message);
      return;
    }

    setServerSync(
      getAutomaticSchedulingStatus({
        whatsappSettingsComplete,
        whatsappRequested,
        hasWhatsappChannel: hasWhatsappChannel || channelIncludesWhatsApp(result.reminder.channel),
        syncing: false,
        synced: true,
        nextRunAt: syncResult.reminder?.next_run_at || nextServerRunAt,
      })
    );
    toast.success(editingId ? "Reminder updated." : "Reminder saved.");
  };

  const startEdit = (reminder) => {
    setEditingId(reminder.id);
    setForm({
      medication_name: reminder.medication_name,
      dose: reminder.dose || "",
      times_per_day: reminder.times_per_day || (reminder.reminder_times?.length || 1),
      reminder_times: reminder.reminder_times || [reminder.reminder_time],
      reminder_time: reminder.reminder_time || reminder.reminder_times?.[0] || "19:30",
      frequency: reminder.frequency,
      specific_days: reminder.specific_days || [],
      once_date: reminder.once_date || "",
      active: reminder.active !== false,
      channel: reminder.channel || V2_REMINDER_CHANNELS.app,
    });
    setErrors({});
  };

  const resetForm = () => {
    setForm(initialForm);
    setEditingId("");
    setErrors({});
  };

  const handleDelete = async (reminder) => {
    const confirmed = window.confirm(`Delete ${reminder.medication_name}?`);
    if (!confirmed) return;

    const result = deleteV2MedicationReminder(reminder.id, { isGuest });
    if (!result.ok) {
      toast.error("Reminder could not be deleted.");
      return;
    }

    setReminders(loadV2MedicationReminders({ isGuest }));
    const syncResult = await deleteV2ServerMedicationReminder(reminder.id);
    if (!syncResult.ok) {
      const message = "Saved on this device, but automatic WhatsApp scheduling could not be activated.";
      setServerSync({ status: V2_SERVER_SYNC_STATUS.unavailable, message, nextRunAt: "" });
      toast.warning(message);
      return;
    }
    toast.success("Reminder deleted.");
  };

  const handleActiveChange = async (reminder, active) => {
    const result = setV2MedicationReminderActive(reminder.id, active, { isGuest });
    if (!result.ok) {
      toast.error("Reminder could not be updated.");
      return;
    }

    setReminders(loadV2MedicationReminders({ isGuest }));
    const syncResult = await setV2ServerReminderActive(reminder.id, active, { isGuest });
    if (!syncResult.ok) {
      const message = "Saved on this device, but automatic WhatsApp scheduling could not be activated.";
      setServerSync({ status: V2_SERVER_SYNC_STATUS.unavailable, message, nextRunAt: "" });
      toast.warning(message);
    }
  };

  const handleReminderEvent = (reminder, status, scheduledFor = "") => {
    const result = createV2ReminderEvent(reminder, status, { isGuest, scheduledFor });
    if (!result.ok) {
      toast.error("Reminder status could not be recorded.");
      return;
    }

    setEvents(loadV2ReminderEvents({ isGuest }));
    toast.success(status === V2_REMINDER_EVENT_STATUS.taken ? "Marked as taken." : "Marked as missed.");
  };

  const handleTestWhatsAppReminder = async (reminder = createTestWhatsAppReminder(settings?.reminders || {})) => {
    setWhatsappStatus(WHATSAPP_UI_STATUS.sending);
    const result = await sendWhatsAppReminderRequest({
      settings: settings.reminders,
      reminder,
      scheduledFor: new Date().toISOString(),
    });

    const message = getWhatsAppUiStatus(result);
    setWhatsappStatus(message);

    if (result.sent) {
      toast.success(message);
    } else if (result.dryRun) {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  const handleEnablePushNotifications = async () => {
    setPushStatus((current) => ({ ...current, busy: true, message: "Enabling push notifications..." }));
    const result = await enablePushNotifications();
    setPushStatus(createPushStatusFromResult(result));

    if (!result.ok) {
      toast.error(result.error || "Push notifications could not be enabled.");
      return;
    }

    const saved = saveSettings({
      ...settings,
      reminders: {
        ...settings.reminders,
        enabled: true,
        notifications_enabled: true,
        push_notifications_enabled: true,
      },
    });
    await syncV2NotificationSettings(saved);
    toast.success("Push notifications enabled on this device.");
  };

  const handleDisablePushNotifications = async () => {
    setPushStatus((current) => ({ ...current, busy: true, message: "Disabling push notifications..." }));
    const result = await disablePushNotifications();
    setPushStatus(createPushStatusFromResult(result));

    const saved = saveSettings({
      ...settings,
      reminders: {
        ...settings.reminders,
        push_notifications_enabled: false,
        notifications_enabled: false,
      },
    });
    await syncV2NotificationSettings(saved);

    if (!result.ok) {
      toast.error(result.error || "Push notifications could not be disabled.");
      return;
    }

    toast.success("Push notifications disabled on this device.");
  };

  const handleSendTestPush = async () => {
    setPushStatus((current) => ({ ...current, busy: true, message: "Sending test push..." }));
    const result = await sendTestPushNotification();
    const reconciled = await reconcilePushSubscription();
    setPushStatus(createPushStatusFromResult(reconciled));

    if (!result.ok) {
      toast.error(result.error || "Test push failed.");
      return;
    }

    toast.success("Test push sent.");
  };

  const toggleAppReminders = (enabled) => {
    saveSettings({
      ...settings,
      reminders: {
        ...settings.reminders,
        enabled,
      },
    });
  };

  const toggleDailySystemReminder = async (enabled) => {
    const saved = saveSettings({
      ...settings,
      reminders: {
        ...settings.reminders,
        daily_system_reminder: {
          ...dailySystemReminder,
          enabled,
        },
      },
    });
    await syncV2NotificationSettings(saved);
    toast.success("Daily reminder updated.");
    return saved;
  };

  const updateDailySystemTime = async (time) => {
    const saved = saveSettings({
      ...settings,
      reminders: {
        ...settings.reminders,
        daily_system_reminder: {
          ...dailySystemReminder,
          time,
        },
      },
    });
    await syncV2NotificationSettings(saved);
  };

  return (
    <section className="mx-auto max-w-7xl space-y-6">
      <header className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-end">
        <div className="space-y-2">
          <p className="text-sm font-semibold text-primary">{copy.brand}</p>
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Reminders</h1>
          <p className="max-w-3xl text-muted-foreground">
            Manage medication reminders and the daily evening review reminder on this device.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card px-4 py-3 text-sm">
          <span className="font-semibold">Notification permission: </span>
          <span className="capitalize text-muted-foreground">{pushStatus.permission.replace("-", " ")}</span>
        </div>
      </header>

      <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <section className="space-y-5">
          <form className="rounded-lg border border-border bg-card p-5" onSubmit={handleSubmit}>
            <SectionHeader icon={Pill} title={editingId ? "Edit Medication Reminder" : "Add Medication Reminder"} />
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <Field label="Medication name" htmlFor="medication-name" error={errors.medication_name}>
                <Input
                  id="medication-name"
                  value={form.medication_name}
                  onChange={(event) => updateForm("medication_name", event.target.value)}
                />
              </Field>
              <Field label="Dose per reminder" htmlFor="medication-dose" description={reminderFormHelp.dose}>
                <Input
                  id="medication-dose"
                  value={form.dose}
                  onChange={(event) => updateForm("dose", event.target.value)}
                />
              </Field>
              <Field
                label="Times per day"
                htmlFor="times-per-day"
                error={errors.times_per_day}
                description={reminderFormHelp.timesPerDay}
              >
                <select
                  id="times-per-day"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.times_per_day}
                  onChange={(event) => updateTimesPerDay(event.target.value)}
                >
                  {V2_REMINDER_TIMES_PER_DAY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {getTimesPerDayLabel(option)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Reminder channel" htmlFor="reminder-channel" error={errors.channel}>
                <select
                  id="reminder-channel"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.channel}
                  onChange={(event) => updateForm("channel", event.target.value)}
                >
                  <option value={V2_REMINDER_CHANNELS.app}>App notification</option>
                  <option value={V2_REMINDER_CHANNELS.whatsapp}>WhatsApp</option>
                  <option value={V2_REMINDER_CHANNELS.both}>Both</option>
                </select>
              </Field>
              <Field
                label="Frequency"
                htmlFor="reminder-frequency"
                error={errors.frequency}
                description={reminderFormHelp.frequency}
              >
                <select
                  id="reminder-frequency"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  value={form.frequency}
                  onChange={(event) => updateForm("frequency", event.target.value)}
                >
                  <option value={V2_REMINDER_FREQUENCIES.daily}>Daily</option>
                  <option value={V2_REMINDER_FREQUENCIES.specificDays}>Specific days</option>
                  <option value={V2_REMINDER_FREQUENCIES.once}>Once</option>
                </select>
              </Field>
              <div className="md:col-span-2">
                <Label>Reminder times</Label>
                <p className="mt-1 text-sm text-muted-foreground">Set each time for the selected reminder schedule.</p>
                <div className="mt-3 grid gap-4 sm:grid-cols-2">
                  {Array.from({ length: Number(form.times_per_day) || 1 }, (_, index) => (
                    <Field
                      key={index}
                      label={`Time ${index + 1}`}
                      htmlFor={`reminder-time-${index}`}
                      error={index === 0 ? errors.reminder_times : undefined}
                    >
                      <Input
                        id={`reminder-time-${index}`}
                        type="time"
                        value={form.reminder_times?.[index] || ""}
                        onChange={(event) => updateReminderTime(index, event.target.value)}
                      />
                    </Field>
                  ))}
                </div>
              </div>
              {form.frequency === V2_REMINDER_FREQUENCIES.once && (
                <Field label="Date" htmlFor="reminder-once-date" error={errors.once_date}>
                  <Input
                    id="reminder-once-date"
                    type="date"
                    value={form.once_date}
                    onChange={(event) => updateForm("once_date", event.target.value)}
                  />
                </Field>
              )}
            </div>

            {form.frequency === V2_REMINDER_FREQUENCIES.specificDays && (
              <div className="mt-4 space-y-2">
                <Label>Days</Label>
                <div className="flex flex-wrap gap-2">
                  {V2_WEEKDAY_KEYS.map((day) => (
                    <Button
                      key={day}
                      type="button"
                      variant={form.specific_days.includes(day) ? "default" : "outline"}
                      onClick={() => toggleSpecificDay(day)}
                    >
                      {weekdayLabels[day]}
                    </Button>
                  ))}
                </div>
                {errors.specific_days && <p className="text-sm font-medium text-destructive">{errors.specific_days}</p>}
              </div>
            )}

            {shouldShowWhatsappNotice(form.channel) && (
              <WhatsappSettingsNotice isValid={formWhatsappValidation.valid} maskedNumber={maskedWhatsAppNumber} />
            )}

            <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}
              <Button type="submit">
                {editingId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editingId ? "Update Reminder" : "Add Reminder"}
              </Button>
            </div>
          </form>

          <section className="rounded-lg border border-border bg-card p-5">
            <SectionHeader icon={Bell} title="Push Notifications" />
            <p className="mt-3 text-sm text-muted-foreground">
              Background Web Push sends generic medication reminders through the service worker, even when the app is not open.
            </p>
            <div className="mt-4 grid gap-3">
              <ToggleRow
                label="Enable app reminder checks"
                checked={appRemindersEnabled}
                onChange={toggleAppReminders}
              />
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <SettingSummary label="Browser support" value={pushStatus.supported ? "Supported" : "Not supported"} tone={pushStatus.supported ? "success" : "warning"} />
              <SettingSummary label="Permission" value={formatPushStatus(pushStatus.permission)} tone={pushStatus.permission === "granted" ? "success" : pushStatus.permission === "denied" ? "warning" : "muted"} />
              <SettingSummary label="Device subscription" value={pushStatus.subscriptionActive ? "Active" : "Inactive"} tone={pushStatus.subscriptionActive ? "success" : "warning"} />
              <SettingSummary label="Background delivery" value={pushStatus.backgroundActive && pushNotificationsEnabled ? "Active" : "Unavailable"} tone={pushStatus.backgroundActive && pushNotificationsEnabled ? "success" : "warning"} />
              <SettingSummary label="Device type" value={pushStatus.platform || "Unknown"} />
            </div>
            {pushStatus.requiresIosInstall && (
              <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
                <ol className="list-decimal space-y-1 pl-5">
                  <li>Open this website in Safari.</li>
                  <li>Tap Share.</li>
                  <li>Tap Add to Home Screen.</li>
                  <li>Open Dialysis Diet Pro from the new Home Screen icon.</li>
                  <li>Return to Reminders and tap Enable push notifications.</li>
                </ol>
              </div>
            )}
            {pushStatus.message && (
              <p className="mt-4 rounded-lg border border-border bg-background p-3 text-sm text-muted-foreground">
                {pushStatus.message}
              </p>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button type="button" variant="outline" disabled={pushStatus.busy || pushStatus.permission === "denied"} onClick={handleEnablePushNotifications}>
                <BellRing className="h-4 w-4" />
                Enable push notifications
              </Button>
              <Button type="button" variant="outline" disabled={pushStatus.busy || !pushStatus.subscriptionActive} onClick={handleSendTestPush}>
                <BellRing className="h-4 w-4" />
                Send test push notification
              </Button>
              <Button type="button" variant="ghost" disabled={pushStatus.busy || !pushStatus.subscriptionActive} onClick={handleDisablePushNotifications}>
                Disable push notifications
              </Button>
            </div>
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <SectionHeader icon={MessageCircle} title="WhatsApp Reminders" />
            <p className="mt-3 text-sm text-muted-foreground">
              WhatsApp reminders require consent and a valid WhatsApp number.
            </p>
            <AutomaticSchedulingStatus sync={serverSync} />
            <div className="mt-4">
              <WhatsappSettingsNotice isValid={whatsappSettingsComplete} maskedNumber={maskedWhatsAppNumber} />
            </div>
            {canShowTestWhatsAppButton && (
              <div className="mt-4">
                <Button type="button" className="w-full sm:w-auto" onClick={() => handleTestWhatsAppReminder()}>
                  <MessageCircle className="h-4 w-4" />
                  {lang === "ar" ? "إرسال تذكير واتساب تجريبي" : "Send test WhatsApp reminder"}
                </Button>
              </div>
            )}
            {whatsappStatus && (
              <p className={getWhatsAppStatusClass(whatsappStatus)}>{whatsappStatus}</p>
            )}
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <SectionHeader icon={CalendarClock} title="Daily Evening Reminder" />
            <p className="mt-3 text-sm text-muted-foreground">{dailySystemReminder.message}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-center">
              <Field label="Time" htmlFor="daily-system-time">
                <Input
                  id="daily-system-time"
                  type="time"
                  value={dailySystemReminder.time}
                  onChange={(event) => updateDailySystemTime(event.target.value)}
                />
              </Field>
              <ToggleRow
                label={dailySystemReminder.enabled ? "Active" : "Paused"}
                checked={dailySystemReminder.enabled}
                onChange={toggleDailySystemReminder}
              />
            </div>
          </section>
        </section>

        <section className="space-y-5">
          {(dueReminders.length > 0 || dueSystemReminder) && (
            <section className="rounded-lg border border-primary/30 bg-primary/5 p-5">
              <SectionHeader icon={BellRing} title="Due Now" />
              <div className="mt-4 space-y-3">
                {dueSystemReminder && (
                  <div className="rounded-lg border border-border bg-background p-4">
                    <h3 className="font-bold">Daily review reminder</h3>
                    <p className="text-sm text-muted-foreground">{dailySystemReminder.message}</p>
                  </div>
                )}
                {dueReminders.map(({ reminder, scheduledFor }) => (
                  <ReminderDueCard
                    key={`${reminder.id}-${scheduledFor}`}
                    reminder={reminder}
                    scheduledFor={scheduledFor}
                    onEvent={handleReminderEvent}
                  />
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-border bg-card p-5">
            <SectionHeader icon={Clock} title="Medication Reminders" />
            {hasWhatsappChannel && (
              <div className="mt-3">
                <WhatsappSettingsNotice isValid={whatsappSettingsComplete} maskedNumber={maskedWhatsAppNumber} />
              </div>
            )}
            {reminders.length > 0 ? (
              <div className="mt-4 space-y-3">
                {reminders.map((reminder) => (
                  <ReminderCard
                    key={reminder.id}
                    reminder={reminder}
                    onEdit={startEdit}
                    onDelete={handleDelete}
                    onActiveChange={handleActiveChange}
                    onEvent={handleReminderEvent}
                    onTestWhatsApp={handleTestWhatsAppReminder}
                    canSendTestWhatsApp={canShowTestWhatsAppButton && channelIncludesWhatsApp(reminder.channel)}
                    testWhatsAppLabel={lang === "ar" ? "إرسال تذكير واتساب تجريبي" : "Send test WhatsApp reminder"}
                    serverNextRunAt={calculateNextReminderRun(reminder, { timezone: getBrowserTimezone() })}
                  />
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                Add medication reminders to track doses on this device.
              </p>
            )}
          </section>

          <section className="rounded-lg border border-border bg-card p-5">
            <SectionHeader icon={CheckCircle2} title="Recent Reminder Events" />
            {recentEvents.length > 0 ? (
              <div className="mt-4 space-y-2">
                {recentEvents.map((event) => (
                  <div key={event.id} className="rounded-lg border border-border bg-background p-3 text-sm">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-semibold">{event.medication_name_snapshot}</span>
                      <span className="capitalize text-muted-foreground">{event.status}</span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{formatEventTime(event.occurred_at)}</p>
                    {event.whatsapp_status && (
                      <p className="mt-1 text-xs font-medium text-muted-foreground">
                        WhatsApp: {event.whatsapp_status.replace("_", " ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 rounded-lg bg-muted p-4 text-sm text-muted-foreground">
                Taken and missed reminder actions will appear here.
              </p>
            )}
          </section>
        </section>
      </div>
    </section>
  );
}

function ReminderCard({
  reminder,
  onEdit,
  onDelete,
  onActiveChange,
  onEvent,
  onTestWhatsApp,
  canSendTestWhatsApp,
  testWhatsAppLabel,
  serverNextRunAt,
}) {
  const nextReminder = calculateNextReminderTime(reminder);

  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="space-y-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="min-w-0 break-words text-lg font-extrabold leading-snug">{reminder.medication_name}</h3>
            <StatusBadge active={reminder.active} />
          </div>
          {reminder.dose && <p className="mt-1 text-sm text-muted-foreground">Dose per reminder: {reminder.dose}</p>}
          <div className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-5">
            <InfoRow label="Frequency" value={getFrequencyLabel(reminder)} />
            <InfoRow label="Times per day" value={getTimesPerDayLabel(reminder.times_per_day)} />
            <InfoRow label="Reminder times" value={getReminderTimesLabel(reminder)} />
            <InfoRow label="Channel" value={getReminderChannelLabel(reminder.channel)} />
            <InfoRow label="Next" value={formatNextReminder(nextReminder)} />
            {channelIncludesWhatsApp(reminder.channel) && (
              <InfoRow label="Server next" value={formatServerReminderTime(serverNextRunAt)} />
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 border-t border-border pt-4">
          <Button type="button" variant="outline" size="sm" className="min-h-9" onClick={() => onEvent(reminder, V2_REMINDER_EVENT_STATUS.taken)}>
            <CheckCircle2 className="h-4 w-4" />
            Taken
          </Button>
          <Button type="button" variant="outline" size="sm" className="min-h-9" onClick={() => onEvent(reminder, V2_REMINDER_EVENT_STATUS.missed)}>
            <XCircle className="h-4 w-4" />
            Missed
          </Button>
          {canSendTestWhatsApp && (
            <Button type="button" variant="outline" size="sm" className="min-h-9" onClick={() => onTestWhatsApp(reminder)}>
              <MessageCircle className="h-4 w-4" />
              {testWhatsAppLabel}
            </Button>
          )}
          <Button type="button" variant="outline" size="sm" className="min-h-9" onClick={() => onEdit(reminder)}>
            <Edit3 className="h-4 w-4" />
            Edit
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="min-h-9"
            onClick={() => onActiveChange(reminder, !reminder.active)}
          >
            <PauseCircle className="h-4 w-4" />
            {reminder.active ? "Pause" : "Activate"}
          </Button>
          <Button type="button" variant="ghost" size="sm" className="min-h-9 text-destructive" onClick={() => onDelete(reminder)}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>
    </article>
  );
}

function AutomaticSchedulingStatus({ sync }) {
  const toneClass =
    sync.status === V2_SERVER_SYNC_STATUS.active
      ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
      : sync.status === V2_SERVER_SYNC_STATUS.syncing
        ? "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-100"
        : "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100";

  return (
    <div className={`mt-4 rounded-lg border p-3 text-sm font-semibold ${toneClass}`}>
      <p>{sync.message}</p>
      <p className="mt-1 text-xs font-medium opacity-80">
        Next server-scheduled reminder: {formatServerReminderTime(sync.nextRunAt)}
      </p>
    </div>
  );
}

function WhatsappSettingsNotice({ isValid, maskedNumber }) {
  if (isValid) {
    return (
      <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100">
        WhatsApp reminder settings are complete{maskedNumber ? ` for ${maskedNumber}` : ""}.
      </p>
    );
  }

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm font-medium text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100">
      <p>WhatsApp reminders require consent and a valid WhatsApp number.</p>
      <Button asChild variant="link" className="mt-1 h-auto p-0 text-amber-900 dark:text-amber-100">
        <Link to="/settings">Open Settings</Link>
      </Button>
    </div>
  );
}

function ReminderDueCard({ reminder, scheduledFor, onEvent }) {
  return (
    <article className="rounded-lg border border-border bg-background p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-extrabold">{reminder.medication_name}</h3>
          <p className="text-sm text-muted-foreground">
            Due at {formatEventTime(scheduledFor)}{reminder.dose ? ` - ${reminder.dose}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" onClick={() => onEvent(reminder, V2_REMINDER_EVENT_STATUS.taken, scheduledFor)}>
            <CheckCircle2 className="h-4 w-4" />
            Taken
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onEvent(reminder, V2_REMINDER_EVENT_STATUS.missed, scheduledFor)}
          >
            <XCircle className="h-4 w-4" />
            Missed
          </Button>
        </div>
      </div>
    </article>
  );
}

function SectionHeader({ icon: Icon, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-lg font-bold">{title}</h2>
    </div>
  );
}

function Field({ label, htmlFor, error, description, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor}>{label}</Label>
      {description && <p className="text-sm text-muted-foreground">{description}</p>}
      {children}
      {error && <p className="text-sm font-medium text-destructive">{error}</p>}
    </div>
  );
}

function ToggleRow({ label, checked, onChange }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 rounded-lg border border-border bg-background px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
          : "bg-slate-100 text-slate-700 dark:bg-slate-900 dark:text-slate-200"
      }`}
    >
      {active ? "Active" : "Paused"}
    </span>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="min-h-[4.5rem] rounded-lg bg-muted px-3 py-2">
      <span className="block text-xs font-medium text-muted-foreground">{label}</span>
      <span className="mt-1 block break-words font-bold leading-snug">{value}</span>
    </div>
  );
}

function SettingSummary({ label, value, tone = "muted" }) {
  const toneClass =
    tone === "success"
      ? "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
      : tone === "warning"
        ? "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
        : "border-border bg-background text-foreground";

  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${toneClass}`}>
      <span className="block text-xs font-medium opacity-75">{label}</span>
      <span className="mt-1 block break-words font-bold">{value}</span>
    </div>
  );
}

function createInitialPushStatus() {
  const support = detectPushSupport();
  return {
    supported: support.supported,
    permission: getPermissionStatus(),
    subscriptionActive: false,
    backgroundActive: false,
    platform: support.platform,
    requiresIosInstall: support.requiresIosInstall,
    busy: false,
    message: support.requiresIosInstall ? "Install the app from Safari before enabling push notifications on iPhone or iPad." : "",
  };
}

function createPushStatusFromResult(result = {}) {
  const support = result.support || detectPushSupport();
  const subscriptionActive = result.status === "active" || Boolean(result.subscription?.id);
  return {
    supported: support.supported,
    permission: support.permission || getPermissionStatus(),
    subscriptionActive,
    backgroundActive: support.supported && subscriptionActive,
    platform: support.platform,
    requiresIosInstall: support.requiresIosInstall,
    busy: false,
    message: result.error || "",
  };
}

function formatPushStatus(value = "") {
  if (!value) return "Default";
  if (value === "unsupported") return "Not supported";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getDueSystemReminder(reminder, now = new Date()) {
  if (!reminder?.enabled || !/^\d{2}:\d{2}$/.test(reminder.time || "")) return null;

  const [hours, minutes] = reminder.time.split(":").map(Number);
  const scheduled = new Date(now);
  scheduled.setHours(hours, minutes, 0, 0);
  const diffMs = now.getTime() - scheduled.getTime();

  if (diffMs >= 0 && diffMs <= 60 * 1000) {
    return { scheduledFor: scheduled.toISOString(), message: reminder.message };
  }

  return null;
}

function formatEventTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

async function sendWhatsAppReminderRequest({ settings, reminder, scheduledFor, dryRun = false }) {
  const payloadResult = createWhatsAppFunctionBody({
    settings,
    reminder,
    scheduledFor,
    dryRun,
  });

  if (!payloadResult.ok) {
    return { ok: false, error: payloadResult.error };
  }

  try {
    const response = await fetch("/.netlify/functions/send-whatsapp-reminder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payloadResult.payload),
    });
    const data = await response.json().catch(() => ({}));

    if (data?.disabled) {
      return { ok: false, disabled: true, provider: "evolution" };
    }

    if (!response.ok || !data?.ok) {
      return { ok: false, provider: "evolution" };
    }

    return {
      ok: true,
      sent: Boolean(data.sent),
      dryRun: Boolean(data.dryRun),
      provider: data.provider || "evolution",
    };
  } catch {
    return { ok: false, provider: "evolution" };
  }
}

function createTestWhatsAppReminder(settings = {}) {
  return {
    id: "test-whatsapp-reminder",
    medication_name: "",
    dose: "",
    active: true,
    channel: channelIncludesWhatsApp(settings.channel) ? settings.channel : V2_REMINDER_CHANNELS.whatsapp,
  };
}

function getWhatsAppUiStatus(result = {}) {
  if (result.sent) return WHATSAPP_UI_STATUS.sent;
  if (result.dryRun) return WHATSAPP_UI_STATUS.dryRun;
  if (result.disabled) return WHATSAPP_UI_STATUS.disabled;
  return WHATSAPP_UI_STATUS.failed;
}

function getWhatsAppStatusClass(status = "") {
  const base = "mt-4 rounded-lg border p-3 text-sm font-semibold";
  if (status === WHATSAPP_UI_STATUS.sent) {
    return `${base} border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100`;
  }
  if (status === WHATSAPP_UI_STATUS.dryRun) {
    return `${base} border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-100`;
  }
  if (status === WHATSAPP_UI_STATUS.disabled || status === WHATSAPP_UI_STATUS.failed) {
    return `${base} border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100`;
  }
  return `${base} border-border bg-muted text-foreground`;
}

function getNextServerRunAt(reminders = []) {
  const candidates = reminders
    .filter((reminder) => channelIncludesWhatsApp(reminder.channel))
    .map((reminder) => calculateNextReminderRun(reminder, { timezone: getBrowserTimezone() }))
    .filter(Boolean)
    .sort();

  return candidates[0] || "";
}

function getAutomaticSchedulingStatus({
  whatsappSettingsComplete,
  whatsappRequested,
  hasWhatsappChannel,
  syncing,
  synced,
  nextRunAt,
}) {
  if ((whatsappRequested || hasWhatsappChannel) && !whatsappSettingsComplete) {
    return {
      status: V2_SERVER_SYNC_STATUS.incomplete,
      message: "WhatsApp settings incomplete",
      nextRunAt: "",
    };
  }

  if (syncing) {
    return {
      status: V2_SERVER_SYNC_STATUS.syncing,
      message: "Automatic scheduling is syncing",
      nextRunAt,
    };
  }

  if (synced && (whatsappRequested || hasWhatsappChannel)) {
    return {
      status: V2_SERVER_SYNC_STATUS.active,
      message: "Automatic WhatsApp scheduling active",
      nextRunAt,
    };
  }

  return {
    status: V2_SERVER_SYNC_STATUS.unavailable,
    message: "Automatic scheduling unavailable",
    nextRunAt: "",
  };
}
